import { useState } from "react";
import { type Anomaly } from "@/models/file-submission.tsx";
import type { QuickSearchIdType } from "@/hooks/useQuickSearchNavigate";
import type {
  CoreDataModel,
  DropDownModel,
  PreschoolModel,
  StateModel,
} from "@/models";

export const RECORD_TYPE_NAMES: Record<string, string> = {
  CLEN: "Classroom Enrollment",
  CLRM: "Classroom",
  STFF: "Staff",
  SASS: "Staff Assignment",
  SCLR: "Staff Classroom",
  FAMI: "Family",
  CHLD: "Child",
  ENRL: "Enrollment",
  LANG: "Language",
  SLNG: "Staff Language",
  ATTN: "Attendance",
};

export type ErrorDetails = {
  Id: number;
  ErrorCode: string;
  DataFields: string;
  ErrorMessagePhase: string;
  ErrorMessageType: string;
  ErrorMessageText: string;
  ErrorMessageDescription: string;
  Name: string;
  Code: string;
  Description: string;
  DisplayOrder: number;
};

export type DiscrepancyDetails = {
  anomaly: Anomaly;
  referenceRecord: Record<string, unknown>;
  errorDetails: ErrorDetails | undefined;
  relatedErrors: Anomaly[];
};

export type ErrorsByRecordType = {
  errors: Anomaly[];
  uniqueErrorCodes: Set<string>;
};

export function getRecordTypeName(type: string): string {
  return RECORD_TYPE_NAMES[type] || type;
}

export function getEntityId(anomaly: Anomaly): string | undefined {
  return (
    anomaly.CSPPID ||
    anomaly.LocalStaffId ||
    anomaly.LocalFamilyId ||
    anomaly.LocalClassroomId
  );
}

export function getEntityIdLabel(anomaly: Anomaly): string {
  if (anomaly.CSPPID) return "CSPPID";
  if (anomaly.LocalStaffId) return "Local Staff ID";
  if (anomaly.LocalFamilyId) return "Local Family ID";
  if (anomaly.LocalClassroomId) return "Local Classroom ID";
  return "ID";
}

export function getEntityIdSearchType(
  anomaly: Anomaly
): QuickSearchIdType | undefined {
  if (anomaly.CSPPID) return "CSPPID";
  if (anomaly.LocalStaffId) return "Staff Local ID";
  if (anomaly.LocalFamilyId) return "Local Family ID";
  if (anomaly.LocalClassroomId) return "Classroom ID";
  return undefined;
}

export function getRecordIdLabel(discrepancy: Anomaly): string {
  switch (discrepancy.ReferenceRecordType) {
    case "FAMI":
      return `Family - ${discrepancy.LocalFamilyId}`;
    case "SASS":
    case "SCLR":
    case "STFF":
      return `Staff - ${discrepancy.LocalStaffId}`;
    case "CLRM":
      return `Classroom - ${discrepancy.LocalClassroomId}`;
    case "CHLD":
    case "CLEN":
    case "ENRL":
    case "LANG":
    case "SLNG":
    case "ATTN":
      return `CSPPID - ${discrepancy.CSPPID}`;
    default:
      return "";
  }
}

export function formatFieldName(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

type DropDownListKey = {
  [K in keyof CoreDataModel]: CoreDataModel[K] extends DropDownModel[]
    ? K
    : never;
}[keyof CoreDataModel];

const FIELD_TO_CORE_LIST: Record<string, DropDownListKey> = {
  // Child
  ChildGender: "genderList",
  ChildRace1: "raceList",
  ChildRace2: "raceList",
  ChildRace3: "raceList",
  ChildRace4: "raceList",
  ChildRace5: "raceList",
  ChildCountryOfBirth: "countryList",
  IEPIFSPIndicator: "IEPorIFSPList",
  IEPorIFSPIndicator: "IEPorIFSPList",
  HomelessDwellingTypeCode: "homelessDwellingTypeCodeList",
  StudentExitCategory: "studentExitCategoryList",
  StudentExitCategoryCode: "studentExitCategoryList",
  EligibilityStatus: "eligibilityStatusList",
  EligibilityStatusCode: "eligibilityStatusList",
  ReasonForService: "reasonForServiceList",
  ReasonForServiceCode: "reasonForServiceList",
  CalWORKs: "calWORKsList",
  CalWORKsCode: "calWORKsList",
  NameSuffix: "nameSuffixList",
  ProgramType: "programTypeList",
  ProgramTypeCode: "programTypeList",
  ProgramDuration: "programDurationList",
  ProgramDurationCode: "programDurationList",
  EducationalProgram: "educationalProgramList",
  EducationalProgramCode: "educationalProgramList",

  // ENRL
  ContractType: "contractTypesOfferedList",
  ContractTypeCode: "contractTypesOfferedList",
  EnrollmentEndReason: "studentExitCategoryList",

  // Staff
  StaffGender: "genderList",
  StaffRace1: "raceList",
  StaffRace2: "raceList",
  StaffRace3: "raceList",
  StaffRace4: "raceList",
  StaffRace5: "raceList",
  StaffCountryOfBirth: "countryList",
  StaffRole: "staffRoleList",
  StaffRoleCode: "staffRoleList",
  StaffPermit: "staffPermitList",
  StaffPermitCode: "staffPermitList",
  Education: "educationList",
  EducationCode: "educationList",
  StaffEducation: "educationList",
  TimeBase: "timeBaseList",
  TimeBaseCode: "timeBaseList",
  WaiverType: "waiverTypes",
  WaiverTypeCode: "waiverTypes",

  // SASS — classroom roles use staffRoleList; non-classroom use their own
  ClassroomRole1: "staffRoleList",
  ClassroomRole2: "staffRoleList",
  ClassroomRole3: "staffRoleList",
  ClassroomRole4: "staffRoleList",
  ClassroomRole5: "staffRoleList",
  NonClassroomRole: "nonClassroomRoleList",
  NonClassroomRoleCode: "nonClassroomRoleList",
  NonClassroomRole1: "nonClassroomRoleList",
  NonClassroomRole2: "nonClassroomRoleList",
  NonClassroomRole3: "nonClassroomRoleList",
  NonClassroomRole4: "nonClassroomRoleList",
  NonClassroomRole5: "nonClassroomRoleList",

  // LANG
  InstrumentLanguagesHeard1: "languagesList",
  InstrumentLanguagesHeard2: "languagesList",
  InstrumentLanguagesHeard3: "languagesList",
  InstrumentLanguagesHeard4: "languagesList",
  InstrumentLanguagesHeard5: "languagesList",
  InstrumentLanguagesUnderstood1: "languagesList",
  InstrumentLanguagesUnderstood2: "languagesList",
  InstrumentLanguagesUnderstood3: "languagesList",
  InstrumentLanguagesUnderstood4: "languagesList",
  InstrumentLanguagesUnderstood5: "languagesList",
  InstrumentLanguagesSpoken1: "languagesList",
  InstrumentLanguagesSpoken2: "languagesList",
  InstrumentLanguagesSpoken3: "languagesList",
  InstrumentLanguagesSpoken4: "languagesList",
  InstrumentLanguagesSpoken5: "languagesList",

  // SLNG
  InterviewHomeLanguage1: "languagesList",
  InterviewHomeLanguage2: "languagesList",
  InterviewHomeLanguage3: "languagesList",
  InterviewHomeLanguage4: "languagesList",
  InterviewHomeLanguage5: "languagesList",
  InterviewMostUsedLanguage: "languagesList",

  // Generic / classroom-language fields
  LanguageCode: "languagesList",
  Language: "languagesList",
  ClassroomLanguage: "classroomLanguages",
  ClassroomLanguageCode: "classroomLanguages",
  ClassroomLanguage1: "classroomLanguages",
  ClassroomLanguage2: "classroomLanguages",
  ClassroomLanguage3: "classroomLanguages",
  LanguageProgramType: "languageProgramType",
  LanguageProgramTypeCode: "languageProgramType",

  // Classroom
  SettingType: "settingTypesOfferedList",
  SettingTypeCode: "settingTypesOfferedList",

  // Family
  VolunteerFrequency: "volunteerFrequencyList",
  VolunteerFrequencyCode: "volunteerFrequencyList",
  FamilySize: "familySizeList",
  FamilySizeCode: "familySizeList",
};

const STATE_FIELDS = new Set([
  "ChildStateOfBirth",
  "StaffStateOfBirth",
  "StateOfBirth",
  "StateCode",
]);

const YES_NO_FIELDS = new Set([
  "ChildEthnicity",
  "ChildEthnicityMissingIndicator",
  "ChildRaceMissingIndicator",
  "StaffEthnicity",
  "StaffEthnicityMissingIndicator",
  "StaffRaceMissingIndicator",
  "StaffWaiver",
  "FamilyIncomeVerification",
  "MHCSOffered",
  "InstrumentOptOut",
  "DLLInstrumentDesignated",
  "DLLTeacherDesignated",
]);

function lookupDropDown(
  list: DropDownModel[] | undefined,
  value: string
): DropDownModel | undefined {
  return list?.find((item) => item.Value === value);
}

function lookupState(
  states: StateModel[] | undefined,
  value: string
): StateModel | undefined {
  return states?.find((s) => s.StateCode === value || s.Code === value);
}

function translateCodeToText(
  fieldName: string,
  value: unknown,
  coreData?: CoreDataModel,
  preschools?: PreschoolModel[],
  leaName?: string
): string | null {
  if (value === null || value === undefined || value === "") return null;
  const stringValue = String(value);

  if (YES_NO_FIELDS.has(fieldName)) {
    if (stringValue === "Y") return "Yes";
    if (stringValue === "N") return "No";
    return null;
  }

  if (fieldName === "ReportingLEA" && leaName) {
    return leaName;
  }

  if (fieldName === "PreschoolCode" && preschools) {
    const match = preschools.find((p) => p.PreschoolCDSCode === stringValue);
    if (match) return match.PreschoolName;
  }

  if (STATE_FIELDS.has(fieldName)) {
    const match = lookupState(coreData?.states, stringValue);
    if (match) return match.Name;
  }

  const listKey = FIELD_TO_CORE_LIST[fieldName];
  if (listKey) {
    const match = lookupDropDown(coreData?.[listKey], stringValue);
    if (match) return match.Text;
  }

  return null;
}

export function formatFieldValue(
  value: unknown,
  fieldName?: string,
  coreData?: CoreDataModel,
  preschools?: PreschoolModel[],
  leaName?: string
): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (fieldName) {
    const translated = translateCodeToText(
      fieldName,
      value,
      coreData,
      preschools,
      leaName
    );
    if (translated !== null) return translated;
  }

  return String(value);
}

export function useDiscrepancyPopup(
  discrepancy: DiscrepancyDetails,
  onClose: () => void
) {
  const { anomaly, referenceRecord, errorDetails, relatedErrors } = discrepancy;
  const [viewMode, setViewMode] = useState<"error" | "createRecord">("error");
  const [errorsExpanded, setErrorsExpanded] = useState(false);

  const missingRecordType = anomaly.DiscrepancyRecordType;
  const entityId = getEntityId(anomaly);
  const affectedFields =
    errorDetails?.DataFields?.split(",").map((f) => f.trim()) || [];
  const recordTypeName = getRecordTypeName(anomaly.ReferenceRecordType);

  const errorsByRecordType: Record<string, ErrorsByRecordType> = {};
  relatedErrors.forEach((err) => {
    const recordType = err.ReferenceRecordType;
    if (!errorsByRecordType[recordType]) {
      errorsByRecordType[recordType] = {
        errors: [],
        uniqueErrorCodes: new Set(),
      };
    }
    errorsByRecordType[recordType].errors.push(err);
    errorsByRecordType[recordType].uniqueErrorCodes.add(err.ErrorCode);
  });

  const handleUpdateClick = () => {
    onClose();
  };

  const handleCreateMissingClick = () => {
    setViewMode("createRecord");
  };

  const handleBackToError = () => {
    setViewMode("error");
  };

  const toggleErrorsExpanded = () => {
    setErrorsExpanded((prev) => !prev);
  };

  const getDefaultsForMissingRecord = (): Record<string, unknown> => ({
    PreschoolCode: (anomaly as any).PreschoolCode || "",
    ProgramYear: (anomaly as any).ProgramYear || "",
    LocalStaffId: anomaly.LocalStaffId || "",
    LocalClassroomId: anomaly.LocalClassroomId || "",
    LocalFamilyId: anomaly.LocalFamilyId || "",
    CSPPID: anomaly.CSPPID || "",
  });

  return {
    anomaly,
    referenceRecord,
    errorDetails,
    relatedErrors,

    viewMode,
    errorsExpanded,

    missingRecordType,
    entityId,
    affectedFields,
    recordTypeName,
    errorsByRecordType,

    handleUpdateClick,
    handleCreateMissingClick,
    handleBackToError,
    toggleErrorsExpanded,
    getDefaultsForMissingRecord,
  };
}
